import React, { ReactElement, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '../../constants';
import { SensorTypes, ThresholdType } from '../../models/commonEnums';
import { ThresholdRange } from '../../models/commonTypeScript';
import { CustomOptions, ThresholdOption } from '../../models/customThresholdModels';
import NumberInput from '../input/Number';
import CustomSlider from '../slider/CustomSlider';
import styles from './ThresholdSlider.module.scss';
import useThresholdSlider from './useThresholdSlider';

export type Props = {
    sensorType: SensorTypes;
    ranges: ThresholdRange[];
    thresholdOptions: ThresholdOption[];
    customOptions?: CustomOptions;
    selectedValues: number[];
    setSelectedValues: (values: number[]) => void;
};

const ThresholdSlider = ({
    sensorType,
    selectedValues,
    setSelectedValues,
    thresholdOptions,
    customOptions,
    ranges,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const { determineColorValue, updateInputValue, thumbColors, normalizeRanges, sliderConfig, isValidNumberInput } =
        useThresholdSlider({
            selectedValues,
            setSelectedValues,
            thresholdOptions,
            customOptions,
        });

    const getDotColor = (thresholdType: ThresholdType): ReactElement => {
        const className =
            determineColorValue(thresholdType) === colors.graphRed ? styles.dotColorRed : styles.dotColorYellow;
        return <div className={className} />;
    };

    return (
        <div className={styles.thresholdSetting}>
            <div className={styles.inputs}>
                <div className={styles.thresholdOptions}>
                    {thresholdOptions?.map((option, index) => {
                        return (
                            <div key={option.type}>
                                <div className={styles.threshold}>
                                    {getDotColor(option.type)}
                                    <div className={styles.thresholdType}>
                                        {sensorType === SensorTypes.temp
                                            ? txt(`ThresholdTypeTemp.${option.type}`)
                                            : txt(`ThresholdType.${option.type}`)}
                                    </div>
                                </div>
                                <div className={styles.marginTop}>
                                    <NumberInput
                                        id={option.type}
                                        validate={!isValidNumberInput(index)}
                                        currentValue={selectedValues[index]?.toString() || ''}
                                        size="small"
                                        minValue={customOptions?.minSelectableValue}
                                        maxValue={customOptions?.maxSelectableValue}
                                        step={customOptions?.thresholdDelta || 1}
                                        onChange={(event: SyntheticEvent<HTMLInputElement>): void => {
                                            updateInputValue(event, index);
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={styles.slider}>
                <CustomSlider
                    value={selectedValues}
                    onChange={(event: Event, newValue: number[]): void => {
                        setSelectedValues(newValue);
                    }}
                    ranges={normalizeRanges(ranges, sliderConfig.min, sliderConfig.max)}
                    min={sliderConfig.min}
                    max={sliderConfig.max}
                    step={sliderConfig.step}
                    thumbColors={thumbColors}
                    showGradient
                    valueLabelDisplay="on"
                    aria-label={txt('CustomThresholds.Slider')}
                />
            </div>
        </div>
    );
};

export default ThresholdSlider;
